import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { AUTH } from "@/constants/testIds";
import { prefetchSitesList } from "@/pages/admin/SitesList";
import TurnstileWidget from "@/components/public/TurnstileWidget";

export default function LoginPage() {
  const nav = useNavigate();
  const { user, login } = useAuth();
  const [email, setEmail] = useState("murad@intentlogic.com");
  const [password, setPassword] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  // Already signed in (cookie still valid)? skip the form.
  if (user && typeof user === "object") return <Navigate to="/admin" replace />;

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await login(email, password, turnstileToken);
      prefetchSitesList(); // warm the Sites list while the dashboard renders
      nav("/admin", { replace: true });
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950 admin-grid-bg p-4">
      <div className="w-full max-w-md">
        <div className="mb-10">
          <div className="font-display text-4xl font-black text-white tracking-tight">
            IL<span className="text-amber-500">/</span>NEWSROOM
          </div>
        </div>

        <form
          onSubmit={submit}
          className="border border-zinc-800 bg-zinc-900/50 backdrop-blur p-6 rounded-sm space-y-4"
        >
          <div>
            <label className="label-tiny block mb-1.5">User</label>
            <input
              data-testid={AUTH.emailInput}
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="admin-input w-full bg-transparent border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-100 placeholder-zinc-600"
              placeholder="you@intentlogic.com"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="label-tiny">Pass</label>
              <Link
                data-testid="auth-forgot-password-link"
                to="/admin/forgot-password"
                className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-amber-500 transition-colors font-mono"
              >
                Forgot?
              </Link>
            </div>
            <input
              data-testid={AUTH.passwordInput}
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="admin-input w-full bg-transparent border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-100"
              placeholder="•••••••••••"
            />
          </div>

          {err && (
            <div
              data-testid={AUTH.errorMessage}
              className="text-xs text-red-400 border border-red-900/60 bg-red-950/40 px-3 py-2 rounded-sm font-mono"
            >
              {err}
            </div>
          )}

          <div data-testid="auth-turnstile-slot">
            <TurnstileWidget onToken={setTurnstileToken} theme="dark" />
          </div>

          <button
            data-testid={AUTH.submitButton}
            type="submit"
            disabled={busy}
            className="w-full bg-white text-zinc-950 hover:bg-zinc-200 disabled:opacity-50 transition-colors py-2.5 rounded-sm text-sm font-semibold flex items-center justify-center gap-2"
          >
            {busy && <Loader2 className="animate-spin" size={14} />}
            Enter Control Room
          </button>
        </form>
      </div>
    </div>
  );
}
